import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gqlErrors';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePaymentFormPerimeterXCaptcha } from '#src/app/shared/hooks/payments';
import type { PaymentType } from '#src/app/shared/utils/payments';
import { setPxDeclineCode, transformPaymentErrorType } from '#src/app/shared/utils/payments';

import { usePromoteFormState } from '../../../formContext/formState';
import { useTracking } from '../useTracking';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useOnTokenError(): (paymentType: PaymentType, err: any) => void {
	const [, { setGeneralErrorMessage, clearLoading }] = usePromoteFormState();
	const { translate } = useI18n();

	const { trackGeneralError, trackPaymentError } = useTracking();

	const triggerPerimeterXCaptcha = usePaymentFormPerimeterXCaptcha('corgi_promote');
	const { errorReporter } = useUtilityContext();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function onTokenError(paymentType: PaymentType, err: any): void {
		clearLoading();
		setGeneralErrorMessage(translate('corgi.co.promote.generic_payment_error'));
		setPxDeclineCode(err);
		if (err instanceof GQLResponsePerimeterXChallengeError) {
			trackGeneralError({ paymentType, errorMessage: 'Human verification required', errorType: 'RequestBlocked' });
			void triggerPerimeterXCaptcha({
				pxResponse: err.pxChallenge,
				operationName: err.operationName,
			});
			// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		} else if (err.type === 'card_error') {
			trackPaymentError({
				paymentType,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				errorMessage: err.message,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				errorType: transformPaymentErrorType({ error: err, paymentType }),
			});
		} else {
			trackGeneralError({
				paymentType,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
				errorMessage: err.message,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				errorType: transformPaymentErrorType({ error: err, paymentType }),
			});
		}
		void errorReporter.report(
			{
				error: new Error('Payment failed due to token error', { cause: err }),
				params: { paymentType },
			},
			{
				severity: 'warning',
			},
		);
	};
}
